/**
 * Input rules from the original app's forms. Forms use them to show the error under each field;
 * services run them again before calling the provider. The backend must apply the same rules (see DataProvider.ts).
 * Every message ends with a full stop.
 */
import type { DeadlineChangeInput, MemberInput, ProjectInput, ProjectLink, ProjectUpdateInput, WorkLogInput } from '@/types/models';
import { isValidDate } from './format';

export type FieldErrors = Record<string, string>;

export const LIMITS = {
  projectName: 60,
  lead: 60,
  service: 120,
  completed: 500,
  pending: 500,
  memberName: 60,
  role: 60,
  work: 500,
  note: 1500,
  metricLabel: 60,
  metricValue: 30,
  linkLabel: 40,
  linkUrl: 500,
  reason: 300,
  reportTitle: 150,
  search: 60,
} as const;

const blank = (v: unknown) => !String(v ?? '').trim();

export function validateProject(p: Partial<ProjectInput>): FieldErrors {
  const e: FieldErrors = {};
  if (blank(p.name)) e.name = 'Project Name is required.';
  return e;
}

export function validateMember(m: Partial<MemberInput>): FieldErrors {
  const e: FieldErrors = {};
  if (blank(m.name)) e.name = 'Full Name is required.';
  return e;
}

export function validateWorkLog(l: Partial<Omit<WorkLogInput, 'hours'>> & { hours?: number | string }): FieldErrors {
  const e: FieldErrors = {};
  if (!isValidDate(l.date)) e.date = 'Date is required.';
  const h = String(l.hours ?? '').trim();
  if (!h || isNaN(Number(h)) || Number(h) < 0) e.hours = 'Hours is required and must be 0 or more.';
  else if (Number(h) > 24) e.hours = 'Hours cannot exceed 24.';
  if (blank(l.memberId)) e.memberId = 'Member is required.';
  return e;
}

export function validateProjectUpdate(u: Partial<ProjectUpdateInput>): FieldErrors {
  const e: FieldErrors = {};
  if (!isValidDate(u.date)) e.date = 'Date is required.';
  if (blank(u.projectId)) e.projectId = 'Project is required.';
  return e;
}

/** `current` is the project's current deadline ('' when none is set). */
export function validateDeadlineChange(d: Partial<DeadlineChangeInput>, current: string): FieldErrors {
  const e: FieldErrors = {};
  if (!isValidDate(d.to) || (current && String(d.to) <= current)) e.to = current ? 'New deadline must be a date after the current deadline.' : 'Deadline is required.';
  if (String(d.reason ?? '').trim().replace(/\s+/g, ' ').length < 10) e.reason = 'A proper reason is required (at least 10 characters).';
  return e;
}

export function validateLink(l: Partial<ProjectLink>): FieldErrors {
  const e: FieldErrors = {};
  if (blank(l.label)) e.label = 'Label is required.';
  if (blank(l.url)) e.url = 'URL is required.';
  return e;
}

export const hasErrors = (e: FieldErrors): boolean => Object.keys(e).length > 0;
